<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Icon from 'next/icon/Icon.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  // eslint-disable-next-line vue/no-unused-properties
  active: {
    type: Boolean,
    default: false,
  },
  inbox: {
    type: Object,
    required: true,
  },
});

const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');

const reauthorizationRequired = computed(() => {
  return props.inbox.reauthorization_required;
});

// Evolution API (WhatsApp via Baileys) inboxes carry connection info on their
// channel's additional_attributes, refreshed every 2 minutes by
// Evolution::PollConnectionStateJob. That job already centralises the
// disconnection rule (immediate on `close`/`closed`, debounced for a stuck
// `connecting`, never alarming on ambiguous poll failures) into the
// `whatsapp_disconnected` boolean, so the UI just reads it. The raw-state
// fallback keeps older rows that predate the boolean working.
const evolutionDisconnected = computed(() => {
  const attrs = props.inbox?.additional_attributes;
  if (!attrs || attrs.provider !== 'evolution') return false;
  if (typeof attrs.whatsapp_disconnected === 'boolean') {
    return attrs.whatsapp_disconnected;
  }
  return ['close', 'closed'].includes(attrs.connection_state);
});

// The leaf itself lives inside a RouterLink that opens the inbox
// conversations. The reconnect badge must instead take the user to the inbox
// settings page (where the Evolution reconnect/QR banner lives), so we stop
// the parent link navigation and push the settings route explicitly.
const goToReconnect = () => {
  router.push({
    name: 'settings_inbox_show',
    params: {
      accountId: accountId.value,
      inboxId: props.inbox.id,
    },
  });
};
</script>

<template>
  <span class="size-4 grid place-content-center rounded-full">
    <ChannelIcon :inbox="inbox" class="size-4" />
  </span>
  <div class="flex-1 truncate min-w-0">{{ label }}</div>
  <div
    v-if="reauthorizationRequired"
    v-tooltip.top-end="$t('SIDEBAR.REAUTHORIZE')"
    class="grid place-content-center size-5 bg-n-ruby-5/60 rounded-full"
  >
    <Icon icon="i-woot-alert" class="size-3 text-n-ruby-9" />
  </div>
  <button
    v-else-if="evolutionDisconnected"
    v-tooltip.top-end="'WhatsApp desconectado — clique para reconectar'"
    type="button"
    class="grid place-content-center size-5 bg-n-ruby-5/60 rounded-full hover:bg-n-ruby-5 cursor-pointer"
    @click.stop.prevent="goToReconnect"
  >
    <Icon icon="i-woot-alert" class="size-3 text-n-ruby-9" />
  </button>
</template>
